import { orderBy } from 'lodash'
import { ChangeEvent, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router'
import IWButton from 'shared/components/thunderbolt/IWButton'
import IWDropdown, {
  DropdownValueProps,
} from 'shared/components/thunderbolt/IWDropdown'
import IWGenericCard from 'shared/components/thunderbolt/IWGenericCard'
import IWTextInput from 'shared/components/thunderbolt/IWTextInput'
import { useToast } from 'shared/components/thunderbolt/IWToastContext'
import IWTypography from 'shared/components/thunderbolt/IWTypography'
import {
  createLocationAttribute,
  getCredentials,
} from 'shared/loadSchedulingClient'
import styled from 'styled-components'

const Wrapper = styled.div`
  display: flex;
  justify-content: left;
  flex-direction: column;
  gap: 1rem;
`

const StyledSectionRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: left;
  flex-direction: column;
  margin: 0 auto 0 0;

  & > * {
    flex: 0 0 calc(25% - 0.75rem); /* 25% width for 4 items, accounting for gap */
  }
`

const StyledFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 0.5rem;
`

const PreferencesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const CardContent = styled.div`
  display: flex;
  padding: 1rem;
  flex-direction: column;
  gap: 1rem;
`

const StyledInfoBlock = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: 0.5rem;
`

const StyledInfoSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`

const MAX_UTILITY_LENGTH = 30

const Section = ({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}) => {
  return (
    <PreferencesSection>
      {title && (
        <IWTypography size="lg" weight="medium">
          {title}
        </IWTypography>
      )}
      <IWGenericCard>
        <CardContent>
          <StyledInfoSection>
            <StyledInfoBlock>{children}</StyledInfoBlock>
          </StyledInfoSection>
        </CardContent>
      </IWGenericCard>
    </PreferencesSection>
  )
}

export default function LsAdminCreateLocationAttributes() {
  const navigate = useNavigate()
  const toast = useToast()
  const { t } = useTranslation()
  const [utility, setUtility] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [credentialOptions, setCredentialOptions] = useState<
    DropdownValueProps[]
  >([])
  const [selectedCredential, setSelectedCredential] =
    useState<DropdownValueProps>()

  const { data: credentials } = useQuery({
    queryKey: ['ls-admin-credentials'],
    queryFn: async () => {
      return getCredentials(true)
    },
  })

  const hasUtilityError = () =>
    utility.length < 2 || utility.length > MAX_UTILITY_LENGTH

  const handleNavigateBack = () => {
    navigate('/admin/load-scheduling/location-attributes')
  }

  const handleCreate = async () => {
    if (!selectedCredential?.value || !utility) {
      return
    }

    setIsLoading(true)

    try {
      await createLocationAttribute(selectedCredential?.value, utility)

      setIsLoading(false)

      handleNavigateBack()
    } catch (error) {
      toast.alert(error?.response?.data, {
        canDismiss: true,
        duration: 10_000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (credentials && credentials.length) {
      const credentialDropdownValues = orderBy(credentials, [
        'market',
        'brand',
        'locationName',
        'location',
      ])
        .filter((c) => c.isActive)
        .map((c) => ({
          label: `${c.market} - ${c.brand} - ${c.locationName} - ${c.location}`,
          value: c.credentialId,
        }))

      setCredentialOptions(credentialDropdownValues)
      setSelectedCredential(credentialDropdownValues[0])
    }
  }, [credentials])

  return (
    <main>
      <Wrapper>
        <Section title={t('loadScheduling.labels.locationAttribute')}>
          <StyledSectionRow>
            <IWDropdown
              fullWidth
              required
              label={t('loadScheduling.labels.orgCredentials')}
              options={credentialOptions}
              value={selectedCredential}
              disabled={credentialOptions?.length === 0}
              onChange={(value: DropdownValueProps) => {
                setSelectedCredential(value)
              }}
            />

            <IWTextInput
              label={t('loadScheduling.labels.utility')}
              maxLength={MAX_UTILITY_LENGTH}
              required
              hasError={hasUtilityError()}
              onKeyUp={(e) => {
                if (hasUtilityError()) {
                  e.preventDefault()
                  return
                }

                if (e.key === 'Enter') {
                  handleCreate()
                }
              }}
              value={utility}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setUtility(e.target.value)
              }}
            />
          </StyledSectionRow>
        </Section>

        <StyledFooter>
          <IWButton color="grey" variant="outline" onClick={handleNavigateBack}>
            {t('button.cancel')}
          </IWButton>

          <IWButton onClick={handleCreate} disabled={hasUtilityError()}>
            {t('button.create')}
          </IWButton>
        </StyledFooter>
      </Wrapper>
    </main>
  )
}
